import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useConsent } from "./ConsentContext";
import {
  ALL_GRANTED,
  CATEGORY_META,
  CATEGORY_ORDER,
  DEFAULT_CATEGORIES,
  type ConsentCategories,
} from "@/lib/consent/types";

export function ConsentSettingsDialog() {
  const { state, isSettingsOpen, closeSettings, saveCustom, acceptAll, rejectAll } = useConsent();
  const [draft, setDraft] = useState<ConsentCategories>(
    state?.categories ?? DEFAULT_CATEGORIES,
  );

  return (
    <Dialog
      open={isSettingsOpen}
      onOpenChange={(o) => {
        if (!o) closeSettings();
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold text-primary">
            Cookie-Einstellungen
          </DialogTitle>
          <DialogDescription className="font-body text-sm leading-relaxed text-foreground">
            Wählen Sie, welche Kategorien von Cookies und Technologien Sie zulassen möchten.
            Essenzielle Dienste sind zum sicheren Betrieb technisch notwendig (Rechtsgrundlage
            § 25 Abs. 2 Nr. 2 TDDDG i. V. m. Art. 6 Abs. 1 lit. f DSGVO) und können nicht
            deaktiviert werden. Alle übrigen Kategorien setzen Ihre Einwilligung nach Art. 6
            Abs. 1 lit. a DSGVO / § 25 Abs. 1 TDDDG voraus. Ihre Einwilligung ist freiwillig
            und kann jederzeit mit Wirkung für die Zukunft widerrufen werden – die Nichtabgabe
            oder der Widerruf hat keine Nachteile für die Nutzung der Website. Details zu den
            einzelnen Diensten, zu Empfängern und zur Übermittlung in Drittländer (u. a. USA
            auf Grundlage des EU-U.S. Data Privacy Frameworks und der Standardvertragsklauseln)
            finden Sie in unserer{" "}
            <Link to="/datenschutz" className="underline underline-offset-4">
              Datenschutzerklärung
            </Link>
            .
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 flex flex-col gap-3">
          {CATEGORY_ORDER.map((cat) => {
            const meta = CATEGORY_META[cat];
            const checked = draft[cat] || !!meta.required;
            return (
              <div
                key={cat}
                className="rounded-md border border-border bg-background p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-display text-base font-extrabold text-primary">
                      {meta.title}
                      {meta.required && (
                        <span className="ml-2 rounded bg-secondary px-2 py-0.5 font-body text-xs font-normal text-foreground">
                          Immer aktiv
                        </span>
                      )}
                    </p>
                    <p className="mt-1 font-body text-sm leading-relaxed text-foreground">
                      {meta.description}
                    </p>
                  </div>
                  <Switch
                    checked={checked}
                    disabled={meta.required}
                    onCheckedChange={(v) =>
                      setDraft((d) => ({ ...d, [cat]: meta.required ? true : v }))
                    }
                    aria-label={`${meta.title} ${checked ? "deaktivieren" : "aktivieren"}`}
                  />
                </div>

                {meta.services.length > 0 && (
                  <details className="mt-3 group">
                    <summary className="cursor-pointer font-display text-xs font-extrabold uppercase tracking-wide text-primary/80 hover:text-primary">
                      Dienste anzeigen ({meta.services.length})
                    </summary>
                    <ul className="mt-3 flex flex-col gap-3">
                      {meta.services.map((s) => (
                        <li
                          key={s.id}
                          className="rounded-md border border-border bg-secondary/40 p-3 font-body text-xs leading-relaxed text-foreground"
                        >
                          <p className="font-display text-sm font-extrabold text-primary">
                            {s.name}
                          </p>
                          <dl className="mt-1 grid grid-cols-[130px_1fr] gap-x-3 gap-y-1">
                            <dt className="text-muted-foreground">Anbieter</dt>
                            <dd>{s.provider}</dd>
                            <dt className="text-muted-foreground">Zweck</dt>
                            <dd>{s.purpose}</dd>
                            <dt className="text-muted-foreground">Speicherdauer</dt>
                            <dd>{s.retention}</dd>
                            <dt className="text-muted-foreground">Rechtsgrundlage</dt>
                            <dd>{s.legalBasis}</dd>
                            {s.thirdCountry && (
                              <>
                                <dt className="text-muted-foreground">Drittland</dt>
                                <dd>{s.thirdCountry}</dd>
                              </>
                            )}
                          </dl>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={rejectAll}
            className="inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-4 text-sm font-extrabold text-foreground transition-colors hover:bg-secondary"
          >
            Alle ablehnen
          </button>
          <button
            type="button"
            onClick={() => saveCustom(draft)}
            className="inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-4 text-sm font-extrabold text-foreground transition-colors hover:bg-secondary"
          >
            Auswahl speichern
          </button>
          <button
            type="button"
            onClick={acceptAll}
            className="inline-flex h-10 items-center justify-center rounded-md bg-brand-button px-4 text-sm font-extrabold text-brand-button-foreground transition-opacity hover:opacity-90"
          >
            Alle akzeptieren
          </button>
        </div>

        {/* "Alle akzeptieren" Quick-Apply ohne State-Roundtrip */}
        <span className="sr-only" aria-hidden>
          {JSON.stringify(ALL_GRANTED)}
        </span>
      </DialogContent>
    </Dialog>
  );
}
